import React, { useState, useRef, useEffect } from 'react';
import { faUser, faSignOutAlt, faUsers, faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ROLE_DISPLAY_NAMES } from '../services/invitationService';

export default function ProfileDropdown(): React.ReactElement {
  const { logout, user, currentOrganization } = useAuth();
  const [profileOpen, setProfileOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');
  const isOwner = currentOrganization?.role === 'owner';

  function handleUsersNavigation(): void {
    navigate('/users');
  }

  function handleOrganizationSettings(): void {
    navigate('/org');
  }

  function handleLogout(): void {
    logout();
    navigate('/login');
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    }

    if (profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [profileOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setProfileOpen(!profileOpen)}
        className="flex items-center space-x-3 p-2 text-sm rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="hidden md:block text-right">
          <p className="text-sm font-medium text-gray-900">{user?.email || 'Usuário'}</p>
          <p className="text-xs text-gray-500 capitalize">
            {isAdminContext
              ? ROLE_DISPLAY_NAMES['saasadmin']
              : ROLE_DISPLAY_NAMES[currentOrganization?.role as keyof typeof ROLE_DISPLAY_NAMES] ||
                'User'}
          </p>
        </div>
        <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full">
          <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-white" />
        </div>
      </button>

      {profileOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">
              Olá,&nbsp;{user?.user_metadata?.name || 'User'}
            </p>
            <p className="text-xs text-gray-500 capitalize">
              {ROLE_DISPLAY_NAMES[currentOrganization?.role as keyof typeof ROLE_DISPLAY_NAMES]}
            </p>
          </div>
          {isOwner && (
            <>
              <button
                onClick={handleOrganizationSettings}
                className="flex items-center w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <FontAwesomeIcon icon={faCog} className="mr-3 h-4 w-4" />
                Configurações
              </button>
              <button
                onClick={handleUsersNavigation}
                className="flex items-center w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <FontAwesomeIcon icon={faUsers} className="mr-3 h-4 w-4" />
                Usuários
              </button>
            </>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="mr-3 h-4 w-4" />
            Sair do Sistema
          </button>
        </div>
      )}
    </div>
  );
}
